<template>
  <div :class="['dn-editor-toplist-collection', backgroundColor?.toLowerCase()]">
    <dn-headline
      v-if="headline"
      class="header"
      :text="headline"
    />
    <dn-set
      v-for="(group, groupIndex) in setGroups"
      :key="groupIndex"
      :layout="group.layout"
      :theme_layout="backgroundColor"
      :items="group.items"
    />
  </div>
</template>
<script setup lang="ts">
import type { PropType } from 'vue';
import type { ToplistFeedItem } from '~/composables/useEditorsToplistCutoff';
import { rankByReadPenalty, READ_PENALTY } from '~/utils/editorReadsUtils';
import type { CardProps, SetGroup, Slot } from '~/types/collection';

const props = defineProps({
  items: {
    type: Array as PropType<ToplistFeedItem[]>,
    required: false,
    default: () => []
  },
  rowlayout: {
    type: String,
    required: false,
    default: 'BBB'
  },
  repeat: {
    type: Number,
    required: false,
    default: 1
  },
  headline: {
    type: String,
    required: false,
    default: null
  },
  backgroundColor: {
    type: String,
    required: false,
    default: null
  },
  type: {
    type: String,
    required: false,
    default: null
  },
  removeUsed: {
    type: Boolean,
    required: false,
    default: false
  },
  id: {
    type: String,
    required: false,
    default: ''
  },
  placeholder: {
    type: String,
    required: false,
    default: ''
  },
  slots: {
    type: Array as PropType<Slot[]>,
    required: false,
    default: () => []
  },
  readPenalty: {
    type: Number as PropType<number | null>,
    required: false,
    default: null
  }
});

provide('teaser_provider', 'articleTeaser');

const filteredItems = ref<ToplistFeedItem[]>([]);

const globalSkipIds = inject('global_skip_ids', ref<string[]>([]));
const globalAwaitForList = inject('global_await_for_list', ref({}));
const globalStatusList = inject('global_status_list', ref({}));

const activeRowLayouts = ref<string[]>([]);
const { bumpIds, onReadsReady } = useEditorReads();

/*
 * The penalty resolves edition -> publication-context -> code constant, in
 * that order: DrEdition supplies read_penalty per toplist placeholder (see
 * fields.read_penalty on the cpp-editortoplist set), publication-context's
 * default.json provides a shared fallback, and READ_PENALTY is the
 * last-resort if neither is configured.
 */
const configPenalty = useRuntimeConfig().public.editorReads?.readPenalty as number | undefined;
const effectivePenalty = props.readPenalty ?? configPenalty ?? READ_PENALTY;

/*
 * Demote stories the logged-in user has already read by effectivePenalty
 * slots (score = original position + penalty) before the layout slice, so
 * unread stories float up but nothing is removed. onReadsReady() defers the
 * one-shot slice/emit until the reads query has settled; bumpIds resolves to
 * an empty set for anonymous users (and on error), so this is a no-op for
 * them and the toplist renders as-is.
 */
const processAndEmit = () => {
  onReadsReady(() => {
    const candidates = props.removeUsed
      ? props.items.filter(item => !globalSkipIds.value.includes(item.id))
      : props.items;

    const ordered = rankByReadPenalty(candidates, bumpIds.value, effectivePenalty);

    const segments = props.rowlayout
      .split(',')
      .map(l => l.trim())
      .filter(Boolean);

    const patternLength = segments.length;
    const repeat = Math.max(1, props.repeat);
    const expandedSegments = patternLength === 0 ? [] : Array.from({ length: repeat }, (_, i) => segments[i % patternLength]);

    const trimmedSegments: string[] = [];
    let total = 0;
    for (const seg of expandedSegments) {
      if (total >= ordered.length) { break; }
      trimmedSegments.push(seg);
      total += layoutItemCount(seg);
    }
    activeRowLayouts.value = trimmedSegments;
    filteredItems.value = ordered.slice(0, total);
    globalSkipIds.value.push(...filteredItems.value.map(item => item.id));

    if (props.placeholder && props.id) {
      useBusEmitter(`DnCollection__collection__${props.placeholder}__${props.id}`, { items: filteredItems.value });
    }
  });
};

onMounted(() => {
  const cid = props.placeholder && props.id ? `${props.placeholder}__${props.id}` : '';
  const priorIds: string[] = (cid && (globalAwaitForList.value as Record<string, string[]>)[cid]) || [];

  if (priorIds.length === 0) {
    processAndEmit();
    return;
  }

  const receivedIds: string[] = [];
  const listenerCleanups: (() => void)[] = [];

  const checkDone = () => {
    if (receivedIds.length === priorIds.length) {
      listenerCleanups.forEach(fn => fn && fn());
      processAndEmit();
    }
  };

  priorIds.forEach((id) => {
    if ((globalStatusList.value as Record<string, { load: boolean }>)[id]?.load === true) {
      receivedIds.push(id);
    } else {
      const cleanup = useBusListener({
        [`DnCollection__collection__${id}`]: () => {
          receivedIds.push(id);
          checkDone();
        }
      });
      listenerCleanups.push(cleanup);
    }
  });

  checkDone();
});

const teaserType = props.type;

const { convertKeys } = useKeyConversion('snake');
const displayItems = computed(() =>
  filteredItems.value.map((item) => {
    /*
     * convertKeys recurses, snake-casing the nested lead asset's image fields
     * (imageCrops -> image_crops etc.) which DnImage expects in camelCase.
     * Restore the original lead asset so editorial crops survive. Runtime feed
     * items are camelCase (leadAsset); fall back to lead_asset for safety.
     */
    const leadAsset = (item as { leadAsset?: typeof item.lead_asset }).leadAsset ?? item.lead_asset;
    return {
      ...convertKeys(item, 'camel'),
      lead_asset: leadAsset,
      video: item.video,
      hide_lead: true,
      loading_mode: 'lazy',
      teaser_type: teaserType,
      features: {
        ...(item.features ?? {}),
        'image-format': 'auto'
      }
    };
  })
);

/*
 * Layouts where the grid slot, not the editor's priority, decides how a card
 * is styled. An item can land in a cell that disagrees with its own priority
 * (an A or A+ item in one of ABB's two trailing B cells, a B item in BBA's trailing
 * A cell, an A item in either of BB's cells, and so on), which renders A
 * styling in a B-sized cell or vice versa.
 */
const SLOT_DRIVEN_LAYOUTS = ['ABB', 'BBA', 'BB'];

/*
 * Re-class every mismatched card to its slot's priority. ABB and BBA move the
 * mobile image position with it: a card demoted into a B cell stacks its image
 * on top, one promoted into the A cell loses the editor's `top`, which that
 * cell isn't designed for. BB's cells are uniform, so it keeps whatever the
 * editor picked — except for an A+, which is too large to leave as-is. Cards
 * already matching their slot are left untouched.
 */
const applyLayoutOverrides = (layout: string, slice: CardProps[]): CardProps[] => {
  const pattern = layout.replace(/[^a-zA-Z]/g, '').toUpperCase();
  if (!SLOT_DRIVEN_LAYOUTS.includes(pattern)) {
    return [...slice];
  }

  return slice.map((item, itemIndex) => {
    const slot = pattern[itemIndex];
    const priority = String(item.priority ?? '').toUpperCase();
    const base = priority.charAt(0);
    if (base === slot || (base !== 'A' && base !== 'B')) {
      return { ...item };
    }

    return {
      ...item,
      priority: slot,
      mobile_image_position: slot === 'B' ? 'top' : 'auto'
    };
  });
};

/*
 * Slice the flat displayItems into one group per layout pattern so each pattern
 * renders its own <dn-set>. displayItems already holds the finished card props,
 * so this only slices and appends slots. The `slots` prop is built per row (via
 * calculateSetRows in slotInjectionProcessor), not per group, so each group
 * claims its own run of slots through a cursor: on mobile a multi-row layout
 * gets one ad slot per row, on desktop calculateSetRows is 1 so each group gets
 * a single slot. DnSet renders the slots via <dn-component>.
 */
const setGroups = computed(() => {
  const items = displayItems.value;
  const layoutPatterns = activeRowLayouts.value;
  const slots = props.slots;

  const groups: SetGroup[] = [];
  let index = 0;
  let slotCursor = 0;

  for (const layout of layoutPatterns) {
    const slice = items.slice(index, index + layoutItemCount(layout));
    if (slice.length === 0) { break; }

    const groupItems: CardProps[] = applyLayoutOverrides(layout, slice);
    const rowCount = calculateSetRows({ layout, items: [] });
    groupItems.push(...slots.slice(slotCursor, slotCursor + rowCount));
    slotCursor += rowCount;

    groups.push({ layout, items: groupItems });
    index += slice.length;
  }

  return groups;
});
</script>
<style lang="scss">
.dn-editor-toplist-collection {
  --dn-collection_gap: var(--ui-gap);

  display: grid;
  gap: var(--row-gap);
}
</style>
